import { useQuery } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  ArrowRight,
  BookOpen,
  Lock,
  Hash,
  Calendar,
  Banknote,
} from 'lucide-react';
import { apiClient } from '../lib/api.js';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card.js';
import { Badge } from '../components/ui/Badge.js';
import { cn, formatBRL, formatDateTime } from '../lib/utils.js';

const accountTypeColor: Record<string, string> = {
  asset: 'bg-blue-500/15 text-blue-300 border-blue-500/30',
  liability: 'bg-amber-500/15 text-amber-300 border-amber-500/30',
  equity: 'bg-purple-500/15 text-purple-300 border-purple-500/30',
  revenue: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30',
  cogs: 'bg-orange-500/15 text-orange-300 border-orange-500/30',
  opex: 'bg-rose-500/15 text-rose-300 border-rose-500/30',
  tax: 'bg-red-500/15 text-red-300 border-red-500/30',
  financial: 'bg-slate-500/15 text-slate-300 border-slate-500/30',
};

const accountTypeLabel: Record<string, string> = {
  asset: 'Ativo',
  liability: 'Passivo',
  equity: 'Patrimônio',
  revenue: 'Receita',
  cogs: 'CMV',
  opex: 'Despesa',
  tax: 'Tributo',
  financial: 'Financeiro',
};

export function LedgerDetail(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const { data, isLoading, error } = useQuery({
    queryKey: ['journal-entry', id],
    queryFn: () => apiClient.journalEntryDetail(id!),
    enabled: !!id,
  });

  if (isLoading) {
    return <div className="p-6 text-muted-foreground">Carregando lançamento...</div>;
  }

  if (error || !data) {
    return (
      <div className="p-6">
        <Link
          to="/financeiro"
          className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-4"
        >
          <ArrowLeft className="h-4 w-4" /> Voltar para Financeiro
        </Link>
        <div className="text-rose-400">Journal entry não encontrado.</div>
      </div>
    );
  }

  const { entry, lines } = data;
  const totalDebit = lines.reduce((s, l) => s + Number(l.debitCents), 0);
  const totalCredit = lines.reduce((s, l) => s + Number(l.creditCents), 0);
  const balanced = totalDebit === totalCredit;

  return (
    <div className="p-6 space-y-6 max-w-[1200px] mx-auto">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-sm">
        <Link
          to="/financeiro"
          className="inline-flex items-center gap-1 text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" /> Financeiro
        </Link>
        <ArrowRight className="h-3 w-3 text-muted-foreground" />
        <span className="font-mono text-xs text-muted-foreground">
          {entry.id.slice(0, 8)}
        </span>
      </div>

      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <BookOpen className="h-6 w-6 text-primary" />
            <h1 className="text-2xl font-bold tracking-tight">
              Journal Entry
            </h1>
            {entry.lockedAt && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-emerald-500/15 text-emerald-300">
                <Lock className="h-3 w-3" /> Lockado
              </span>
            )}
            {balanced ? (
              <Badge variant="outline" className="bg-emerald-500/15 text-emerald-300 border-emerald-500/30">
                ✓ Balanceado
              </Badge>
            ) : (
              <Badge variant="outline" className="bg-rose-500/15 text-rose-300 border-rose-500/30">
                ⚠ Desbalanceado
              </Badge>
            )}
          </div>
          <div className="text-sm text-muted-foreground">{entry.description}</div>
        </div>
        <div className="text-right">
          <div className="text-2xl font-bold tabular-nums">{formatBRL(totalDebit)}</div>
          <div className="text-xs text-muted-foreground">{lines.length} linhas</div>
        </div>
      </div>

      {/* Metadata */}
      <div className="grid md:grid-cols-3 gap-3">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-xs text-muted-foreground mb-1">
              <Calendar className="h-3.5 w-3.5" />
              Competência
            </div>
            <div className="text-sm font-medium tabular-nums">
              {formatDateTime(entry.competencia)}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-xs text-muted-foreground mb-1">
              <Banknote className="h-3.5 w-3.5" />
              Caixa
            </div>
            <div className="text-sm font-medium tabular-nums">
              {entry.caixa ? formatDateTime(entry.caixa) : '—'}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-xs text-muted-foreground mb-1">Source</div>
            <Badge variant="outline" className="text-[10px]">
              {entry.sourceType}
            </Badge>
            {entry.sourceEventId && (
              <div className="text-[10px] text-muted-foreground mt-1 font-mono truncate">
                event: {entry.sourceEventId}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Hash chain */}
      <Card>
        <CardContent className="p-4 space-y-2">
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Hash className="h-3.5 w-3.5" />
            <span className="uppercase tracking-wide">Hash Chain (imutabilidade)</span>
          </div>
          <div className="font-mono text-[10px] text-muted-foreground break-all">
            <span className="text-foreground">self:</span> {entry.hashSelf}
          </div>
          <div className="font-mono text-[10px] text-muted-foreground break-all">
            <span className="text-foreground">idempotency:</span> {entry.idempotencyKey}
          </div>
        </CardContent>
      </Card>

      {/* Linhas */}
      <Card>
        <CardHeader>
          <CardTitle>Lançamentos (Dupla Entrada)</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <table className="w-full text-sm">
            <thead className="border-b border-border bg-muted/30">
              <tr className="text-left text-xs text-muted-foreground">
                <th className="px-4 py-2 font-medium">Conta</th>
                <th className="px-4 py-2 font-medium">Tipo</th>
                <th className="px-4 py-2 font-medium">Dimensões</th>
                <th className="px-4 py-2 font-medium text-right">Débito</th>
                <th className="px-4 py-2 font-medium text-right">Crédito</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border/40">
              {lines.map((l) => (
                <tr key={l.id} className="hover:bg-muted/30">
                  <td className="px-4 py-3">
                    <div className="font-mono text-xs text-muted-foreground">
                      {l.accountCode}
                    </div>
                    <div className="text-sm font-medium">{l.accountName}</div>
                  </td>
                  <td className="px-4 py-3">
                    <Badge
                      variant="outline"
                      className={cn('text-[10px]', accountTypeColor[l.accountType])}
                    >
                      {accountTypeLabel[l.accountType] ?? l.accountType}
                    </Badge>
                  </td>
                  <td className="px-4 py-3 text-xs text-muted-foreground space-y-0.5">
                    {l.product && <div>produto: {l.product}</div>}
                    {l.channel && <div>canal: {l.channel}</div>}
                    {l.campaignId && (
                      <div className="font-mono text-[10px]">camp: {l.campaignId}</div>
                    )}
                    {l.creativeId && (
                      <div className="font-mono text-[10px]">creative: {l.creativeId}</div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums">
                    {Number(l.debitCents) > 0 ? (
                      <span className="font-medium text-blue-400">
                        {formatBRL(l.debitCents)}
                      </span>
                    ) : (
                      <span className="text-muted-foreground/40">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums">
                    {Number(l.creditCents) > 0 ? (
                      <span className="font-medium text-emerald-400">
                        {formatBRL(l.creditCents)}
                      </span>
                    ) : (
                      <span className="text-muted-foreground/40">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot className="border-t-2 border-border bg-muted/50">
              <tr className="font-semibold">
                <td colSpan={3} className="px-4 py-3 text-right text-muted-foreground">
                  Totais
                </td>
                <td className="px-4 py-3 text-right tabular-nums text-blue-400">
                  {formatBRL(totalDebit)}
                </td>
                <td className="px-4 py-3 text-right tabular-nums text-emerald-400">
                  {formatBRL(totalCredit)}
                </td>
              </tr>
            </tfoot>
          </table>
        </CardContent>
      </Card>

      {entry.notes && (
        <Card>
          <CardHeader>
            <CardTitle>Notas</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm whitespace-pre-wrap">{entry.notes}</p>
          </CardContent>
        </Card>
      )}

      <div className="text-xs text-muted-foreground border-t border-border pt-4">
        <strong>Imutabilidade:</strong> {entry.lockedAt
          ? `Este lançamento foi lockado em ${formatDateTime(entry.lockedAt)} e não pode mais ser alterado (regra R5).`
          : 'Este lançamento ainda não foi lockado. Pode ser ajustado via lançamento contrário (nunca DELETE/UPDATE).'}
      </div>
    </div>
  );
}
